import {
  Component,
  OnInit,
  PLATFORM_ID,
  inject,
  AfterViewInit,
  DestroyRef,
  ChangeDetectorRef,
  NgZone,
} from '@angular/core';
import { FormGroup, NonNullableFormBuilder, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import * as UserActions from '../../shared/core/state/users/user.actions';
import { State } from '../../shared/core/state';
import {
  createGoogleOnboardingForm,
  emailPhoneValidator,
  hasFieldError,
  isFieldInvalid,
  isUnderAge,
} from '../../shared/util/auth.validators';
import {
  AuthCredentialsDTO,
  UserStatus,
} from '../../shared/core/state/users/models';
import { AuthService } from '../../shared/services/auth.service';
import { isPlatformServer } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { environment } from '../../../environments/environment';
import { ToastService } from '@black/ui';
import { PageLoaderService } from '../../shared/util/pageloader.service';
import { TenantService } from '../../shared/services/tenant.service';
import { finalize } from 'rxjs';

declare const google: any;

@Component({
  selector: 'black-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  standalone: false,
  host: {
    ngSkipHydration: 'true',
  },
})
export class LoginComponent implements OnInit, AfterViewInit {
  private fb = inject(NonNullableFormBuilder);
  private store = inject<Store<State>>(Store);
  private authService = inject(AuthService);
  private platformId = inject(PLATFORM_ID);
  private http = inject(HttpClient);
  private router = inject(Router);
  private toastService = inject(ToastService);
  private destroyRef = inject(DestroyRef);
  private pageLoaderService = inject(PageLoaderService);
  private ngZone = inject(NgZone);
  private cdr = inject(ChangeDetectorRef);
  private tenantService = inject(TenantService);

  loginForm: FormGroup;
  loginError: string | null;
  isServer: boolean;
  isGoogleLoading = false;
  showPassword = false;

  // Google Onboarding Modal Control
  showGoogleOnboarding = false;
  googleIdToken: string | null = null;
  googleOnboardingForm: FormGroup;

  constructor() {
    this.loginForm = this.fb.group({
      username: [
        '',
        {
          validators: [Validators.required, emailPhoneValidator],
        },
      ],
      password: ['', Validators.required],
    });
    this.loginError = null;
    this.isServer = isPlatformServer(this.platformId);

    // Google Onboarding Form
    this.googleOnboardingForm = createGoogleOnboardingForm(
      this.fb,
      this.destroyRef,
      this.cdr,
    );
  }

  ngOnInit(): void {
    if (!this.isServer) {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    }
    this.store.dispatch(UserActions.clearUserState());

    this.store
      .select((state) => state.users)
      .subscribe((users) => {
        if (users.error && users.error !== '') {
          this.loginError = users.error;
        }
      });
  }

  ngAfterViewInit(): void {
    // Only initialize Google Sign-In on client side
    if (!this.isServer && typeof google !== 'undefined') {
      setTimeout(() => {
        this.initializeGoogleSignIn();
      }, 0);
    }
  }

  get isGoogleLoginEnabled(): boolean {
    return this.tenantService.isGoogleLoginEnabled();
  }

  initializeGoogleSignIn(): void {
    if (!this.isGoogleLoginEnabled) {
      return;
    }

    const activeGoogleClientId = this.tenantService.getGoogleClientId();

    // Skip initialization if Google Client ID is not configured
    if (
      !activeGoogleClientId ||
      activeGoogleClientId.includes('YOUR_GOOGLE_CLIENT_ID')
    ) {
      console.warn(
        'Google Client ID not configured. Google Sign-In is disabled. See GOOGLE_SIGNIN_INTEGRATION.md for setup.',
      );
      return;
    }

    try {
      google.accounts.id.initialize({
        client_id: activeGoogleClientId,
        callback: (response: any) =>
          this.ngZone.run(() => this.handleGoogleSignIn(response)),
        ux_mode: 'popup',
        itp_support: true,
      });

      const buttonContainer = document.getElementById('google-signin-button');
      if (buttonContainer) {
        const containerWidth = buttonContainer.offsetWidth || 384;
        google.accounts.id.renderButton(buttonContainer, {
          theme: 'filled_blue',
          size: 'large',
          text: 'signin_with',
          width: Math.min(containerWidth, 384),
        });
      }
    } catch (error) {
      console.error('Error initializing Google Sign-In:', error);
    }
  }

  handleGoogleSignIn(response: any): void {
    const idToken = response.credential;

    this.isGoogleLoading = true;
    this.loginError = null;
    this.pageLoaderService.open();

    // Send to backend for authentication
    this.http
      .post(`${environment.host}/api/v1/auth/google-signin`, { idToken })
      .pipe(
        finalize(() => {
          this.isGoogleLoading = false;
          this.pageLoaderService.close();
        }),
      )
      .subscribe({
        next: (result: any) => {
          if (result.needsOnboarding) {
            this.googleIdToken = idToken;
            this.showGoogleOnboarding = true;
          } else if (result.status === UserStatus.PENDING_PARENTAL_CONSENT) {
            this.toastService.showToast(
              result.message || 'Account pending parent/guardian consent.',
              'info',
            );
            this.router.navigate(['/auth/pending-consent']);
          } else if (result.user && result.token) {
            // Dispatch login success action
            this.store.dispatch(
              UserActions.loginSuccess({
                response: {
                  user: result.user,
                  token: result.token,
                  refreshToken: result.refreshToken,
                },
              }),
            );

            // Navigate to dashboard
            this.router.navigate(['/dashboard']);
          }
        },
        error: (error) => {
          console.error('Google sign-in failed:', error);
          this.loginError =
            error.error?.message || 'Google sign-in failed. Please try again.';
        },
      });
  }

  submitGoogleOnboarding(): void {
    if (!this.googleOnboardingForm.valid || !this.googleIdToken) {
      this.googleOnboardingForm.markAllAsTouched();
      return;
    }

    this.isGoogleLoading = true;
    this.loginError = null;
    this.pageLoaderService.open();

    const dateOfBirth = this.googleOnboardingForm.value.dateOfBirth;
    const parentEmail = this.googleOnboardingForm.value.parentEmail;

    this.http
      .post(`${environment.host}/api/v1/auth/google-signin`, {
        idToken: this.googleIdToken,
        dateOfBirth,
        parentEmail,
      })
      .pipe(
        finalize(() => {
          this.isGoogleLoading = false;
          this.pageLoaderService.close();
        }),
      )
      .subscribe({
        next: (result: any) => {
          this.showGoogleOnboarding = false;
          if (result.status === UserStatus.PENDING_PARENTAL_CONSENT) {
            this.toastService.showToast(
              result.message ||
                'Account created. Pending parent/guardian consent.',
              'info',
            );
            this.router.navigate(['/auth/pending-consent']);
          } else if (result.user && result.token) {
            this.store.dispatch(
              UserActions.loginSuccess({
                response: {
                  user: result.user,
                  token: result.token,
                  refreshToken: result.refreshToken,
                },
              }),
            );
            this.router.navigate(['/dashboard']);
          }
        },
        error: (error) => {
          console.error('Google sign-in onboarding failed:', error);
          this.loginError =
            error.error?.message || 'Google sign-in failed. Please try again.';
        },
      });
  }

  get isGoogleUnder16(): boolean {
    return isUnderAge(this.googleOnboardingForm.get('dateOfBirth')?.value, 16);
  }

  // ====== Validation Helper Methods ======
  hasFieldError(
    form: FormGroup,
    fieldName: string,
    errorType: string,
  ): boolean {
    return hasFieldError(form, fieldName, errorType);
  }

  isFieldInvalid(form: FormGroup, fieldName: string): boolean {
    return isFieldInvalid(form, fieldName);
  }

  get username() {
    return this.loginForm.get('username');
  }

  get password() {
    return this.loginForm.get('password');
  }

  submit() {
    this.username?.setValue(this.username.value.trim());
    this.loginForm.markAllAsTouched();

    if (this.loginForm.valid) {
      this.loginError = null;
      this.store.dispatch(
        UserActions.login({
          credentials: new AuthCredentialsDTO(this.loginForm.value),
        }),
      );
    } else {
      this.loginForm.markAllAsTouched();
    }
  }
}
